import type { Mapping, MappingItem } from '@pn/core/domain/data';
import { type Filter } from '@pn/core/domain/query';
import { UnitSystem } from '@pn/core/domain/types';
import { convertSymbol, toSIUnit, unitToString } from '@pn/core/domain/units';
import { assert } from '@pn/core/utils/assert';
import { smartTitlecase } from '@pn/services/utils/string';
import { isNil, isNumber, isString } from 'lodash-es';

export function filtersToTitle(
  filters: Filter[],
  mapping: Mapping,
  unitSystem: UnitSystem
): string {
  return filters
    .filter((filter) => !isNil(filter.value))
    .map((f) => {
      const mappingItem = mapping[f.field];
      assert(mappingItem, `Mapping not found for field ${f.field}`);
      const value = formatFilterValue(f, mappingItem, unitSystem);

      switch (f.operator) {
        case '=':
          return `=${value}`;
        case '<=':
        case 'onOrBefore':
          return `<${value}`;
        case '>=':
        case 'onOrAfter':
          return `>${value}`;
        case 'not':
        case 'notContains':
          return `NOT ${value}`;
        case 'isNil':
          return `${mappingItem.ui.label} NOT SET`;
        default:
          return value;
      }
    })
    .join(', ');
}

function formatFilterValue(
  filter: Filter,
  mappingItem: MappingItem,
  unitSystem: UnitSystem
): string {
  if (isNil(filter.value)) return '-';

  if (mappingItem.domainType === 'boolean') {
    const label = truncateBooleanLabel(mappingItem.ui.label);
    return filter.value ? `✔ ${label}` : `⨉ ${label}`;
  } else if (mappingItem.domainType === 'SIUnit') {
    assert(isNumber(filter.value), 'filter value must be a number');

    const unit = toSIUnit({
      value: filter.value, // this value must not be converted
      symbol: convertSymbol(
        mappingItem.domainTypeAttributes.symbol,
        unitSystem
      ),
    });

    return unitToString(unit, { displaySymbol: true });
  } else if (isString(filter.value)) {
    return smartTitlecase(filter.value);
  } else {
    return filter.value.toString();
  }
}

function truncateBooleanLabel(label: string): string {
  return label.replace(/^(Has|Is) /, '');
}
